import {
  getData,
  updateData,
  setData,
  createWithKey,
  removeData,
  subscribeToData,
  uploadFile
} from './api-wrapper.service';
import { database, storage } from './core';
import { getAuth } from 'firebase/auth';
import {
  ref,
  query,
  orderByChild,
  equalTo,
  get} from 'firebase/database';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { updateUserStats } from './profile.service';
import { checkAchievements, displayEarnedAchievements } from './achievementTriggers.service';
import goalsService from '../goals.service';
import { calculateImageAspectRatio } from '../../utils/image-helpers';
import logger from '../../utils/logger';
import { TrainingFlow } from '../../types/training';
import { updateTechniquesFromTrainingFlow } from './trainingWiki.service';
import { validateTrainingSession, ValidationResult } from '../../utils/validation';
import { addNewFlow } from './flow.service';
import { healthSyncService } from '../health/health-sync.service';
import { Capacitor } from '@capacitor/core';

// Types
// Match original app exactly: 'training' | 'seminar' | 'camp' | 'teaching'
export type TrainingType = 'training' | 'seminar' | 'camp' | 'teaching';

export interface Training {
  id: string;
  owner: string;
  title?: string;
  date: number;
  dateCreated: number;
  dateModified?: number;
  timezoneOffset?: number;
  location?: {
    name: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    country?: string;
    locality?: string;
    placeID?: string;
  };
  instructor?: string; // Use string (uid) for backward compatibility
  instructorUid?: string;
  techniques?: Record<string, boolean>; // Simplified to match original
  flows?: TrainingFlow[];
  type: TrainingType;
  totalTrainedTime: number;
  rollTime?: number;
  note?: string; // Match original field name
  gi?: boolean; // Add missing field from original
  usedGiRef?: string; // Add missing field from original
  trainedStyle?: string; // Add missing field from original
  customMetrics?: Record<string, any>; // Add missing field from original
  rounds?: Array<{ status: 'won' | 'lost' | 'flow'; partnerName?: string; }>; // Match original structure
  photos?: Record<string, {  // Original app format ONLY
    url: string;
    aspectRatio: number;
    caption?: string;
    dateAdded?: number;
    uri?: null;
  }>; // Original app saves photos as object with numeric keys: { "0": {...}, "1": {...} }
  trainingPhoto?: {  // Legacy main photo field
    url: string;
    aspectRatio: number;
  };
  videosYT?: Record<string, string>; // Match original field name
  videoMetadata?: Record<string, { title?: string; dateAdded?: number }>;
  likes?: Record<string, boolean>;
  // CRITICAL: Match original app field names for social function compatibility
  likesCount?: number;    // Social functions expect this field name
  commentsCount?: number; // Social functions expect this field name
  // Legacy fields for backward compatibility during transition
  likeCount?: number;     // @deprecated - use likesCount
  commentCount?: number;  // @deprecated - use commentsCount
  taggedFriends?: Record<string, {
    firstName?: string;
    lastName?: string;
    username?: string;
    profilePhotoURL?: string;
    isInstructor?: boolean;
  }>;
  shownOnFeed?: boolean; // Match original field name (singular)
  isPublic?: boolean;

  // Health sync metadata - matches original Marune app exactly
  healthProvidersMetadata?: {
    sync: {
      'ios-healthKit'?: boolean;
      'android-googleFit'?: boolean;
    };
  };
}

export interface TrainingInput {
  owner: string;
  title?: string;
  date: number;
  timezoneOffset?: number;
  location?: {
    name: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    country?: string;
    locality?: string;
    placeID?: string;
  };
  instructor?: string; // Use string (uid) for backward compatibility
  instructorUid?: string;
  techniques?: Record<string, boolean>; // Simplified to match original
  flows?: TrainingFlow[];
  type: TrainingType;
  totalTrainedTime: number;
  rollTime?: number;
  note?: string; // Match original field name
  gi?: boolean; // Add missing field from original
  usedGiRef?: string; // Add missing field from original
  trainedStyle?: string; // Add missing field from original
  customMetrics?: Record<string, any>; // Add missing field from original
  rounds?: Array<{ status: 'won' | 'lost' | 'flow'; partnerName?: string; }>; // Match original structure
  photos?: Array<{
    file: File;
    aspectRatio?: number;
    caption?: string;
  }>;
  trainingPhoto?: {  // Legacy main photo field
    file: File;
    aspectRatio?: number;
  };
  videosYT?: Array<{
    youtubeId: string;
    title?: string;
  }>; // Match original field name
  taggedFriends?: Record<string, {
    firstName?: string;
    lastName?: string;
    username?: string;
    profilePhotoURL?: string;
    isInstructor?: boolean;
  }>;
  shownOnFeed?: boolean; // Match original field name (singular)
  isPublic?: boolean;
  skipAchievements?: boolean; // Skip achievement checking for imports
  // Import metadata - NEW Phoenix feature (set when imported from Apple Health)
  importedFrom?: {
    provider: string;
    sourceApp: string;
    sourceBundleId: string;
    originalUuid: string;
    importedAt: number;
  };
}

export interface QueryOptions {
  limit?: number;
  startAfter?: number;
  endBefore?: number;
  startDate?: number;  // Added for compatibility with different naming conventions
  endDate?: number;    // Added for compatibility with different naming conventions
  orderBy?: 'date' | 'dateCreated' | 'totalTrainedTime' | 'feeling' | 'owner';
  orderDirection?: 'asc' | 'desc';
  types?: Array<Training['type']>;
  withPhotos?: boolean;
  withVideos?: boolean;
  withTechniques?: boolean;
  bypassCache?: boolean;
  filterBy?: {         // Added for filtered queries
    field: string;
    value: any;
  };
}

export interface TrainingStats {
  totalSessions: number;
  totalTrainingTime: number;
  totalRollTime: number;
  totalDrillingTime: number;
  totalConditioningTime: number;
  averageTrainingTime: number;
  averageRollTime: number;
  weeklyAverage: number;
  monthlyAverage: number;
  streak: {
    current: number;
    longest: number;
  };
  sessionsLastWeek: number;
  sessionsLastMonth: number;
  sessionsThisYear: number;
  byType: Record<string, number>;
  byLocation: Record<string, number>;
  byInstructor: Record<string, number>;
}

/**
 * Maximum number of retry attempts for failed operations
 */
const MAX_RETRIES = 3;

/**
 * Base delay between retries in milliseconds
 */
const RETRY_DELAY = 1000;

/**
 * In-memory cache for training data to improve performance and offline support
 */
const trainingsCache: {
  byUser: Record<string, { 
    trainings: Training[], 
    timestamp: number,
    expiresAt: number 
  }>,
  byId: Record<string, { 
    training: Training, 
    timestamp: number,
    expiresAt: number 
  }>
} = {
  byUser: {},
  byId: {}
};

/**
 * Cache expiration time in milliseconds (30 minutes)
 */
const CACHE_EXPIRATION = 30 * 60 * 1000;

/**
 * Remove a training from cache after deletion
 * Exported so trainingSync.service.ts can clear cache when training is deleted
 */
export const removeTrainingFromCache = (trainingId: string, ownerId?: string) => {
  // Remove from byId cache
  delete trainingsCache.byId[trainingId];
  logger.info(`Removed training ${trainingId} from byId cache`);

  // Remove from byUser cache if ownerId provided
  if (ownerId && trainingsCache.byUser[ownerId]) {
    trainingsCache.byUser[ownerId].trainings = trainingsCache.byUser[ownerId].trainings
      .filter(t => t.id !== trainingId);
    logger.info(`Removed training ${trainingId} from byUser cache for user ${ownerId}`);
  }

  // Also remove from all user caches (in case ownerId not provided)
  Object.keys(trainingsCache.byUser).forEach(userId => {
    const cache = trainingsCache.byUser[userId];
    const before = cache.trainings.length;
    cache.trainings = cache.trainings.filter(t => t.id !== trainingId);
    if (cache.trainings.length < before) {
      logger.info(`Removed training ${trainingId} from byUser cache for user ${userId}`);
    }
  });
};

/**
 * Clear cached items that have expired
 */
const cleanupCache = () => {
  const now = Date.now();
  
  // Clean user trainings cache
  Object.keys(trainingsCache.byUser).forEach(userId => {
    if (trainingsCache.byUser[userId].expiresAt < now) {
      delete trainingsCache.byUser[userId];
    }
  });
  
  // Clean individual training cache
  Object.keys(trainingsCache.byId).forEach(trainingId => {
    if (trainingsCache.byId[trainingId].expiresAt < now) {
      delete trainingsCache.byId[trainingId];
    }
  });
};

/**
 * Clear all cached training data
 * Should be called on logout to prevent data mixing between users
 */
export const clearTrainingCache = () => {
  logger.info('Clearing all training cache data');
  trainingsCache.byUser = {};
  trainingsCache.byId = {};
};

/**
 * Retry a database operation with exponential backoff
 * @param operation Function to retry
 * @param maxRetries Maximum number of retries
 * @param delay Base delay between retries in ms
 * @returns Promise with the operation result
 */
const retryOperation = async <T>(
  operation: () => Promise<T>, 
  maxRetries = MAX_RETRIES, 
  delay = RETRY_DELAY
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry if not a permission error or network issue
      const isRetryable = 
        error.code === 'PERMISSION_DENIED' || 
        error.code === 'NETWORK_ERROR' ||
        error.message?.includes('network') ||
        error.name === 'AbortError';
        
      if (!isRetryable) {
        throw error;
      }
      
      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        break;
      }
      
      // Wait with exponential backoff before retry
      const waitTime = delay * Math.pow(2, attempt);
      logger.info(`Retrying operation in ${waitTime}ms (attempt ${attempt + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
  
  throw lastError;
};

/**
 * Get friend's public training data using original app's access patterns
 * This function handles viewing other users' trainings by using individual training access
 * instead of bulk queries which may not work for other users due to Firebase rules
 */
export const getFriendTrainings = async (friendUID: string, options?: QueryOptions): Promise<Training[]> => {
  try {
    logger.info(`Fetching friend's trainings for user: ${friendUID}`);
    
    // First, try to get friend's public training IDs from their feed
    const friendFeedData = await getData(`/feeds/${friendUID}`);
    if (!friendFeedData) {
      logger.info(`No feed data found for friend: ${friendUID}`);
      return [];
    }
    
    // Find training post IDs in the feed
    const trainingPostIds = Object.keys(friendFeedData)
      .filter(key => friendFeedData[key].postType === 'training');
    
    if (trainingPostIds.length === 0) {
      logger.info(`No training posts found in feed for friend: ${friendUID}`);
      return [];
    }
    
    logger.info(`Found ${trainingPostIds.length} training post IDs in friend's feed`);
    
    // Fetch each public training individually.
    // ⚠️ PRIVACY MODEL: the `isPublic` check below is CLIENT-SIDE ONLY. The
    // deployed RTDB rules grant `.read` on /trainings/$key to ANY authenticated
    // user — they do NOT enforce isPublic. So this filter is a UX convenience,
    // not a security boundary: a determined client can read any training
    // directly via the Firebase REST API. Server-side privacy is enforced only
    // in the FastAPI search layer (postgres is_public filter). Any NEW direct
    // RTDB read path MUST re-apply this `isPublic !== false` filter, and true
    // confidentiality requires tightening the RTDB rules (see
    // .full-review/SECURITY_RULES_PROPOSAL.md, finding SC1).
    const trainingPromises = trainingPostIds.map(async (id) => {
      try {
        const trainingData = await getData(`/trainings/${id}`);
        if (trainingData && trainingData.owner === friendUID && trainingData.isPublic !== false) {
          return {
            id,
            ...trainingData
          };
        }
        return null;
      } catch (err) {
        logger.warn(`Failed to fetch friend's training ${id}:`, err);
        return null;
      }
    });
    
    const results = await Promise.all(trainingPromises);
    const validTrainings = results.filter(t => t !== null) as Training[];
    
    logger.info(`Successfully fetched ${validTrainings.length} public trainings for friend: ${friendUID}`);
    
    // Apply filters and sort if options provided
    if (options) {
      return applyTrainingFilters(validTrainings, options);
    }
    
    // Default sort by date descending
    return [...validTrainings].sort((a, b) => b.date - a.date);
  } catch (error) {
    logger.error(`Error fetching friend's trainings for ${friendUID}:`, error);
    // Fallback: return empty array instead of throwing (original app behavior)
    return [];
  }
};

// Get user trainings
export const getTrainings = async (uid: string, options?: QueryOptions): Promise<Training[]> => {
  try {
    // Check if we have a cache hit and it's still valid
    const now = Date.now();
    const cacheKey = uid;
    
    if (
      trainingsCache.byUser[cacheKey] && 
      trainingsCache.byUser[cacheKey].expiresAt > now && 
      !options?.bypassCache
    ) {
      logger.info('Using cached training data for user:', uid);
      let cachedTrainings = [...trainingsCache.byUser[cacheKey].trainings];
      
      // Apply filters to cached data
      if (options) {
        cachedTrainings = applyTrainingFilters(cachedTrainings, options);
      } else {
        // Default sort by date descending
        cachedTrainings.sort((a, b) => b.date - a.date);
      }
      
      return cachedTrainings;
    }
    
    // Clean up expired cache entries
    cleanupCache();
    
    // Try to get data with optimized query if possible
    let userTrainings: Training[] = [];
    
    // First, try a different approach - get user's feed to find training IDs
    let foundViaFeed = false;
    try {
      const userFeedData = await getData(`/feeds/${uid}`);
      if (userFeedData) {
        const trainingIds = Object.keys(userFeedData)
          .filter(key => userFeedData[key].postType === 'training');
        
        if (trainingIds.length > 0) {
          logger.info(`Found ${trainingIds.length} training IDs from user feed`);
          
          // Fetch each training individually
          const trainingPromises = trainingIds.map(async (id) => {
            try {
              const trainingData = await getData(`/trainings/${id}`);
              if (trainingData && trainingData.owner === uid) {
                return {
                  id,
                  ...trainingData
                };
              }
              return null;
            } catch (err) {
              logger.warn(`Failed to fetch training ${id}:`, err);
              return null;
            }
          });
          
          const results = await Promise.all(trainingPromises);
          userTrainings = results.filter(t => t !== null) as Training[];
          foundViaFeed = true;
          logger.info(`Successfully fetched ${userTrainings.length} trainings via feed`);
        }
      }
    } catch (feedError) {
      logger.warn('Could not fetch trainings via feed:', feedError);
    }
    
    // If feed approach didn't work, try the query approach
    if (!foundViaFeed) {
      try {
        // Try to query by owner directly (more efficient)
        // Use the proper Firebase Modular SDK syntax
        const trainingsRef = ref(database, 'trainings');
        const trainingsQuery = query(
          trainingsRef,
          orderByChild('owner'),
          equalTo(uid)
        );

        const snapshot = await retryOperation(() =>
          get(trainingsQuery)
        );
        
        if (snapshot.exists()) {
          snapshot.forEach((childSnapshot) => {
            userTrainings.push({
              id: childSnapshot.key!,
              ...childSnapshot.val()
            });
          });
        }
      } catch (queryError) {
        logger.warn('Optimized query failed:', queryError);
        
        // Fall back - just return empty array since we can't query all trainings
        logger.error('Cannot query trainings - Firebase index may be missing for orderByChild("owner")');
        logger.error('Please add this index to your Firebase database rules:');
        logger.error('{');
        logger.error('  "rules": {');
        logger.error('    "trainings": {');
        logger.error('      ".indexOn": ["owner", "date", "dateCreated"]');
        logger.error('    }');
        logger.error('  }');
        logger.error('}');
        userTrainings = [];
      }
    }
    
    // Update cache
    trainingsCache.byUser[cacheKey] = {
      trainings: userTrainings,
      timestamp: now,
      expiresAt: now + CACHE_EXPIRATION
    };
    
    // Also cache individual trainings
    userTrainings.forEach(training => {
      trainingsCache.byId[training.id] = {
        training,
        timestamp: now,
        expiresAt: now + CACHE_EXPIRATION
      };
    });
    
    // Apply filters and sort
    if (options) {
      return applyTrainingFilters(userTrainings, options);
    }
    
    // Default sort by date descending
    return [...userTrainings].sort((a, b) => b.date - a.date);
  } catch (error) {
    logger.error('Error fetching trainings:', error);
    throw error;
  }
};

/**
 * Apply filters, sorting, and pagination to training data
 */
const applyTrainingFilters = (trainings: Training[], options: QueryOptions): Training[] => {
  let filteredTrainings = [...trainings];
  
  // Filter by type
  if (options.types && options.types.length > 0) {
    filteredTrainings = filteredTrainings.filter(t => options.types?.includes(t.type));
  }
  
  // Filter by date range - check both startDate and startAfter for compatibility
  if (options.startDate !== undefined || options.startAfter !== undefined) {
    const startTime = options.startDate !== undefined ? options.startDate : options.startAfter;
    filteredTrainings = filteredTrainings.filter(t => t.date >= startTime!);
  }
  
  // Filter by date range - check both endDate and endBefore for compatibility
  if (options.endDate !== undefined || options.endBefore !== undefined) {
    const endTime = options.endDate !== undefined ? options.endDate : options.endBefore;
    filteredTrainings = filteredTrainings.filter(t => t.date <= endTime!);
  }
  
  // Filter by specific field if filterBy is specified
  if (options.filterBy) {
    filteredTrainings = filteredTrainings.filter(t => {
      const fieldPath = options.filterBy!.field.split('.');
      let value: any = t;
      
      // Navigate through nested fields
      for (const field of fieldPath) {
        value = value[field];
        if (value === undefined) return false;
      }
      
      return value === options.filterBy!.value;
    });
  }
  
  // Filter by media presence
  if (options.withPhotos) {
    filteredTrainings = filteredTrainings.filter(t => t.photos && Object.keys(t.photos).length > 0);
  }
  
  if (options.withVideos) {
    filteredTrainings = filteredTrainings.filter(t => t.videosYT && Object.keys(t.videosYT).length > 0); // Use correct field name
  }
  
  if (options.withTechniques) {
    filteredTrainings = filteredTrainings.filter(t => t.techniques && Object.keys(t.techniques).length > 0);
  }
  
  // Apply sorting
  const orderField = options.orderBy || 'date';
  const isAsc = options.orderDirection === 'asc';
  
  filteredTrainings.sort((a, b) => {
    const aValue = a[orderField as keyof Training] as number;
    const bValue = b[orderField as keyof Training] as number;
    
    if (isAsc) {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });
  
  // Apply limit
  if (options.limit) {
    filteredTrainings = filteredTrainings.slice(0, options.limit);
  }
  
  return filteredTrainings;
};

// Get trainings by date range
export const getTrainingsByDateRange = async (uid: string, startDate: number, endDate: number): Promise<Training[]> => {
  try {
    // First get all trainings for the user
    const allTrainings = await getTrainings(uid);
    
    // Then filter by date range in memory
    const filteredTrainings = allTrainings.filter(training => {
      const trainingDate = training.date || training.dateCreated || 0;
      return trainingDate >= startDate && trainingDate <= endDate;
    });
    
    // Sort by date descending
    filteredTrainings.sort((a, b) => {
      const dateA = a.date || a.dateCreated || 0;
      const dateB = b.date || b.dateCreated || 0;
      return dateB - dateA;
    });
    
    logger.info(`Found ${filteredTrainings.length} trainings between ${new Date(startDate).toISOString()} and ${new Date(endDate).toISOString()}`);
    
    return filteredTrainings;
  } catch (error) {
    logger.error('Error fetching trainings by date range:', error);
    throw error;
  }
};

// Get trainings by technique
export const getTrainingsByTechnique = async (uid: string, techniqueId: string): Promise<Training[]> => {
  try {
    const trainings = await getTrainings(uid);

    // Filter trainings that include the specified technique
    // Handle both old format (boolean) and new format (full technique object)
    return trainings.filter(training => {
      if (!training.techniques) return false;
      const techValue = training.techniques[techniqueId];
      // Support both: { key: true } (old) and { key: { name, sortname, ... } } (new)
      return techValue === true || (typeof techValue === 'object' && techValue !== null);
    });
  } catch (error) {
    logger.error('Error fetching trainings by technique:', error);
    throw error;
  }
};

// Get trainings by location
export const getTrainingsByLocation = async (uid: string, locationId: string): Promise<Training[]> => {
  try {
    const trainings = await getTrainings(uid);
    
    // Filter trainings by location (original uses placeID, not id)
    return trainings.filter(training => training.location?.placeID === locationId);
  } catch (error) {
    logger.error('Error fetching trainings by location:', error);
    throw error;
  }
};

// Get trainings by instructor
export const getTrainingsByInstructor = async (uid: string, instructorId: string): Promise<Training[]> => {
  try {
    const trainings = await getTrainings(uid);
    
    // Filter trainings by instructor (original format is string uid)
    return trainings.filter(training => training.instructor === instructorId);
  } catch (error) {
    logger.error('Error fetching trainings by instructor:', error);
    throw error;
  }
};

// Search trainings by text
export const searchTrainings = async (uid: string, searchText: string): Promise<Training[]> => {
  try {
    if (!searchText || searchText.length < 2) {
      return [];
    }
    
    const searchTermLower = searchText.toLowerCase();
    const trainings = await getTrainings(uid);
    
    // Search in various text fields
    return trainings.filter(training => {
      // Search in title
      if (training.title && training.title.toLowerCase().includes(searchTermLower)) {
        return true;
      }
      
      // Search in notes (use 'note' field to match original)
      if (training.note && training.note.toLowerCase().includes(searchTermLower)) {
        return true;
      }
      
      // Search in location
      if (training.location?.name.toLowerCase().includes(searchTermLower)) {
        return true;
      }
      
      // Search in instructor (original uses string uid, not object)
      if (training.instructor && typeof training.instructor === 'string') {
        // For string instructor (uid), we'd need to resolve the name - skip for now
        // In a full implementation, you'd look up instructor name by uid
      }
      
      // Search in techniques (original format is Record<string, boolean>)
      if (training.techniques) {
        const techniqueIds = Object.keys(training.techniques);
        for (const techniqueId of techniqueIds) {
          if (techniqueId.toLowerCase().includes(searchTermLower)) {
            return true;
          }
        }
      }
      
      // Search in tags (not in original app)
      /*
      if (training.tags && training.tags.some(tag => tag.toLowerCase().includes(searchTermLower))) {
        return true;
      }
      */
      
      return false;
    });
  } catch (error) {
    logger.error('Error searching trainings:', error);
    throw error;
  }
};

// Get specific training by ID
export const getTrainingById = async (trainingId: string, options?: { bypassCache?: boolean }): Promise<Training | null> => {
  try {
    // Check if we have a cache hit and it's still valid
    const now = Date.now();
    
    if (
      trainingsCache.byId[trainingId] && 
      trainingsCache.byId[trainingId].expiresAt > now && 
      !options?.bypassCache
    ) {
      logger.info('Using cached training data for training:', trainingId);
      return trainingsCache.byId[trainingId].training;
    }
    
    // Clean up expired cache entries
    cleanupCache();
    
    // Fetch training data with retry
    const trainingData = await retryOperation(() => 
      getData(`/trainings/${trainingId}`)
    );
    
    if (!trainingData) {
      return null;
    }
    
    const training = {
      id: trainingId,
      ...trainingData
    };
    
    // Update cache
    trainingsCache.byId[trainingId] = {
      training,
      timestamp: now,
      expiresAt: now + CACHE_EXPIRATION
    };
    
    return training;
  } catch (error) {
    logger.error(`Error fetching training ${trainingId}:`, error);
    throw error;
  }
};

// Helper function: Upload photos to existing training (matches original app pattern)
// Photos are uploaded AFTER training creation to avoid validation issues with File objects
// Original app saves photos as an OBJECT with numeric keys at /trainings/{id}/photos: { "0": {...}, "1": {...} }
const uploadPhotosToTraining = async (
  trainingId: string,
  photos: Array<{ file: File; aspectRatio?: number; caption?: string; }>
): Promise<void> => {
  const photosArray: Array<{ url: string; aspectRatio: number; caption: string; dateAdded: number; uri: null }> = [];

  for (const photo of photos) {
    // Validate file object before upload
    if (!photo.file || !photo.file.name || !photo.file.size) {
      logger.warn('Skipping invalid photo - missing file, name, or size');
      continue;
    }

    // Upload photo to Firebase Storage (matches original app storage path)
    // NOTE: No leading slash - Firebase Storage REST API expects paths without leading slash
    const photoPath = `trainings/${trainingId}/photos/${Date.now()}_${photo.file.name}`;
    const photoURL = await uploadFile(photoPath, photo.file);

    // Calculate aspect ratio if not provided
    let aspectRatio = photo.aspectRatio || 1;
    if (!photo.aspectRatio && photo.file) {
      try {
        aspectRatio = await calculateImageAspectRatio(photo.file);
      } catch (error) {
        logger.warn('Failed to calculate aspect ratio for training photo:', error);
      }
    }

    // Add to photos array (matches original app structure exactly)
    // Original app includes: url, aspectRatio, caption, dateAdded, uri: null
    photosArray.push({
      url: photoURL,
      aspectRatio: aspectRatio,
      caption: photo.caption || '',
      dateAdded: Date.now(),
      uri: null
    });
  }

  // Save photos as object with numeric keys (matches original app exactly)
  // Original app: { "0": {url, aspectRatio}, "1": {...} }
  if (photosArray.length > 0) {
    const photosObject: Record<string, { url: string; aspectRatio: number; caption: string; dateAdded: number; uri: null }> = {};
    photosArray.forEach((photo, index) => {
      photosObject[index.toString()] = photo;
    });
    await setData(`/trainings/${trainingId}/photos`, photosObject);
  }
};

// Helper function: Upload legacy trainingPhoto to existing training
const uploadLegacyTrainingPhoto = async (
  trainingId: string,
  trainingPhoto: { file: File; aspectRatio?: number; }
): Promise<void> => {
  // Validate file object
  if (!trainingPhoto.file || !trainingPhoto.file.name || !trainingPhoto.file.size) {
    logger.warn('Skipping invalid legacy trainingPhoto - missing file, name, or size');
    return;
  }

  // Upload trainingPhoto to Firebase Storage
  // NOTE: No leading slash - Firebase Storage REST API expects paths without leading slash
  const photoPath = `trainings/${trainingId}/trainingPhoto_${Date.now()}_${trainingPhoto.file.name}`;
  const photoURL = await uploadFile(photoPath, trainingPhoto.file);

  // Calculate aspect ratio if not provided
  let aspectRatio = trainingPhoto.aspectRatio || 1;
  if (!trainingPhoto.aspectRatio) {
    try {
      aspectRatio = await calculateImageAspectRatio(trainingPhoto.file);
    } catch (error) {
      logger.warn('Failed to calculate aspect ratio for training photo:', error);
    }
  }

  // Update training with trainingPhoto
  await updateData(`/trainings/${trainingId}`, {
    trainingPhoto: {
      url: photoURL,
      aspectRatio: aspectRatio
    }
  });
};

// Monotonic counter mixed into video keys so two videos added in the same
// millisecond can't collide (a bare Date.now() key silently overwrote the
// previous video — guaranteed for multi-video batches in addVideosToTraining).
// Same pattern as photoKeySeq below: Date.now()*1000 + counter stays a numeric
// string well below Number.MAX_SAFE_INTEGER, so parseInt-based sorting of
// media keys keeps working and keys remain valid opaque map keys.
let videoKeySeq = 0;

// Helper function: Add videos to existing training (matches original app pattern)
const addVideosToTraining = async (
  trainingId: string,
  videosYT: Array<{ youtubeId: string; title?: string; }>
): Promise<void> => {
  const videoUpdates: Record<string, any> = {};

  for (const video of videosYT) {
    if (!video.youtubeId) {
      logger.warn('Skipping video without youtubeId');
      continue;
    }

    // Collision-proof, parseInt-sortable key (see videoKeySeq above)
    const videoId = (Date.now() * 1000 + (videoKeySeq++ % 1000)).toString();
    // Original app stores videos as simple string mapping (id -> youtubeId)
    videoUpdates[`/trainings/${trainingId}/videosYT/${videoId}`] = video.youtubeId;
  }

  // Update training with videos using setData (not updateData) since we're setting primitive string values
  if (Object.keys(videoUpdates).length > 0) {
    for (const [path, data] of Object.entries(videoUpdates)) {
      await setData(path, data);
    }
  }
};

// Add new training
export const addTraining = async (data: TrainingInput): Promise<Training> => {
  try {
    // Validate training session data before saving
    const validationResult: ValidationResult = validateTrainingSession({
      totalTrainedTime: data.totalTrainedTime,
      rollTime: data.rollTime,
      date: data.date,
      type: data.type,
      location: data.location
    });
    
    if (!validationResult.isValid) {
      const errorMessage = `Training validation failed: ${validationResult.errors.join(', ')}`;
      logger.error(errorMessage);
      throw new Error(errorMessage);
    }
    
    // Create base training data without photos/videos
    const { photos, trainingPhoto, videosYT, ...trainingData } = data;
    
    // Calculate timezone offset if not provided
    const timezoneOffset = data.timezoneOffset ?? new Date(data.date).getTimezoneOffset() / 60;
    
    // Extract techniques from flows
    // CRITICAL: Original app stores FULL technique objects, not just { key: true }
    // This allows displaying technique names without additional Firebase lookups
    let extractedTechniques: Record<string, any> = {};
    let extractedTechniqueDetails: Record<string, { name: string; isTrainingWikiTech: boolean }> = {};
    if (data.flows && data.flows.length > 0) {
      data.flows.forEach(flow => {
        Object.entries(flow.referencedTechs).forEach(([techId, techRef]) => {
          // Store full technique object (matching original app structure)
          extractedTechniques[techId] = {
            key: techId,
            name: techRef.name,
            sortname: techRef.sortname || techRef.name.toLowerCase(),
            type: techRef.type || 'technique',
            ...(techRef.isTrainingWikiTech && { isTrainingWikiTech: true }),
            ...(techRef.isDefault !== undefined && { isDefault: techRef.isDefault }),
            ...(techRef.user && { user: techRef.user })
          };
          extractedTechniqueDetails[techId] = {
            name: techRef.name,
            isTrainingWikiTech: techRef.isTrainingWikiTech || false
          };
        });
      });
    }
    
    // Add creation timestamp and match original app structure
    const showOnFeed = data.shownOnFeed !== false;

    // IMPORTANT: Process flows before saving training (match original app)
    // For new flows (no key), save to /flows/{uid}/{key} and attach key
    // For existing flows (has key), keep as-is
    const processedFlows: TrainingFlow[] = [];
    if (data.flows && data.flows.length > 0) {
      logger.info(`Processing ${data.flows.length} flows for training`);
      for (const flow of data.flows) {
        if (flow.key) {
          // Existing flow with key - keep as-is
          processedFlows.push(flow);
        } else {
          // New flow without key - save to /flows and get key
          try {
            const flowKey = await addNewFlow(flow);
            processedFlows.push({
              ...flow,
              key: flowKey
            });
            logger.info(`Saved new flow with key: ${flowKey}`);
          } catch (flowError: any) {
            logger.error('Error saving flow:', flowError?.message || flowError);
            // Still add the flow without key rather than failing entire training
            processedFlows.push(flow);
          }
        }
      }
    }

    const baseTrainingData = {
      ...trainingData,
      timezoneOffset,
      dateCreated: Date.now(),
      shownOnFeed: showOnFeed,
      shownOnFeeds: showOnFeed,
      isPublic: data.isPublic !== false,
      techniques: extractedTechniques, // Always extract from flows
      // Ensure all original fields are present with defaults
      gi: data.gi ?? true, // Default to gi=true (matches original app for BJJ/Judo)
      trainedStyle: data.trainedStyle,
      customMetrics: data.customMetrics ?? {},
      flows: processedFlows, // Use processed flows with keys
      rounds: data.rounds
    };
    
    // Create training record at root trainings path (like original app)
    // IMPORTANT: Create training FIRST without photos/videos (matches original app behavior)
    logger.info(`Creating training without media for user ${data.owner}`);
    const id = await createWithKey('/trainings', baseTrainingData);
    logger.info(`Training created with ID: ${id}`);

    // STEP 2: Upload photos AFTER training exists (matches original app pattern)
    // This prevents "Missing required fields" errors and matches the original app's
    // proven sequential upload approach: create training -> upload media
    if (photos && photos.length > 0) {
      logger.info(`Uploading ${photos.length} photos for training ${id}`);
      try {
        await uploadPhotosToTraining(id, photos);
        logger.info(`Successfully uploaded ${photos.length} photos`);
      } catch (photoError) {
        // Log but don't fail training creation if photo upload fails
        logger.error('Error uploading photos (training still created):', photoError);
      }
    }

    // STEP 3: Upload legacy trainingPhoto if provided (after training exists)
    if (trainingPhoto && trainingPhoto.file) {
      logger.info(`Uploading legacy trainingPhoto for training ${id}`);
      try {
        await uploadLegacyTrainingPhoto(id, trainingPhoto);
        logger.info('Successfully uploaded legacy trainingPhoto');
      } catch (photoError) {
        logger.error('Error uploading legacy trainingPhoto (training still created):', photoError);
      }
    }

    // STEP 4: Add videos AFTER training exists (matches original app pattern)
    if (videosYT && videosYT.length > 0) {
      logger.info(`Adding ${videosYT.length} videos for training ${id}`);
      try {
        await addVideosToTraining(id, videosYT);
        logger.info(`Successfully added ${videosYT.length} videos`);
      } catch (videoError) {
        logger.error('Error adding videos (training still created):', videoError);
      }
    }
    
    // Update user stats
    await updateUserTrainingStats(data.owner);
    
    // Update Personal Wiki with techniques from flows
    if (extractedTechniques && Object.keys(extractedTechniques).length > 0) {
      try {
        logger.info(`Updating personal wiki with ${Object.keys(extractedTechniques).length} techniques for user ${data.owner}`);
        logger.info('Technique IDs:', Object.keys(extractedTechniques));
        logger.info('Technique Details:', extractedTechniqueDetails);
        await updateTechniquesFromTrainingFlow(data.owner, Object.keys(extractedTechniques), extractedTechniqueDetails);
        logger.info('Successfully updated personal wiki');
      } catch (wikiError) {
        // Log but don't fail the training creation if wiki update fails
        logger.error('Error updating personal wiki from training flow:', wikiError);
      }
    } else {
      logger.info('No techniques extracted from flows to update in personal wiki');
    }
    
    // Update goal progress (skip for imports)
    if (!data.skipAchievements && data.totalTrainedTime && data.owner) {
      try {
        await goalsService.updateGoalProgressFromTraining(data.owner, data.totalTrainedTime, data.date);
      } catch (goalError) {
        // Log but don't fail the training creation if goal tracking fails
        logger.error('Error updating goal progress:', goalError);
      }
    }
    
    // Check for achievements (skip for imports)
    if (!data.skipAchievements) {
      try {
        const earnedAchievements = await checkAchievements('training_added', { id, trainingData: data });
        displayEarnedAchievements(earnedAchievements);
      } catch (achievementError) {
        // Log but don't fail the training creation if achievement checking fails
        logger.error('Error checking achievements:', achievementError);
      }
    }

    // STEP 6: Sync to Apple Health (iOS only, if enabled)
    // Skip for imports (they came from Health already) and only on iOS
    if (!data.importedFrom && Capacitor.getPlatform() === 'ios') {
      try {
        const isHealthEnabled = await healthSyncService.isProviderEnabled();
        if (isHealthEnabled) {
          logger.info(`Syncing training ${id} to Apple Health`);

          // Build training object for sync (we have all the data)
          const trainingForSync = {
            id,
            date: data.date,
            totalTrainedTime: data.totalTrainedTime,
            trainedStyle: data.trainedStyle,
          };

          const syncSuccess = await healthSyncService.syncTraining(trainingForSync);

          if (syncSuccess) {
            // Update training with health sync metadata (matches original app format)
            const providerId = healthSyncService.getCurrentProviderId();
            const healthMetadata = healthSyncService.makeHealthProvidersMetadata(
              {}, // No existing metadata for new training
              { [providerId]: true }
            );

            await updateData(`/trainings/${id}`, healthMetadata);
            logger.info(`Training ${id} synced to Apple Health successfully`);
          } else {
            logger.warn(`Failed to sync training ${id} to Apple Health`);
          }
        }
      } catch (healthError) {
        // Log but don't fail the training creation if health sync fails
        logger.error('Error syncing to Apple Health:', healthError);
      }
    }

    // Return complete training with ID
    const completeTraining = await getTrainingById(id);
    if (!completeTraining) {
      throw new Error('Failed to fetch created training');
    }
    
    return completeTraining;
  } catch (error) {
    logger.error('Error adding training:', error);
    throw error;
  }
};

// Update training
export const updateTraining = async (id: string, data: Partial<Training>): Promise<Training> => {
  try {
    // SECURITY: Verify user authorization before allowing updates
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to update training');
    }

    // Get existing training to verify ownership
    const existingTraining = await getTrainingById(id);
    if (!existingTraining) {
      throw new Error('Training not found');
    }

    // CRITICAL SECURITY CHECK: Only allow users to update their own trainings
    if (existingTraining.owner !== currentUser.uid) {
      throw new Error('Unauthorized: You can only update your own trainings');
    }

    // Validate training session data if key fields are being updated
    if (data.totalTrainedTime !== undefined || data.rollTime !== undefined || data.date !== undefined) {
      
      const validationData = {
        totalTrainedTime: data.totalTrainedTime ?? existingTraining.totalTrainedTime,
        rollTime: data.rollTime ?? existingTraining.rollTime,
        date: data.date ?? existingTraining.date,
        type: data.type ?? existingTraining.type,
        location: data.location ?? existingTraining.location
      };
      
      const validationResult: ValidationResult = validateTrainingSession(validationData);
      
      if (!validationResult.isValid) {
        const errorMessage = `Training update validation failed: ${validationResult.errors.join(', ')}`;
        logger.error(errorMessage);
        throw new Error(errorMessage);
      }
    }
    
    // Don't update id, owner, or creation dates. Media fields (photos,
    // videosYT, trainingPhoto) are also stripped: the RTDB update() merge
    // must never touch stored media — edits sync media via explicit
    // add/delete calls instead. trainingPhoto in particular could arrive as
    // { file: File } from the edit path, which would serialize as garbage
    // and clobber a legacy main photo if it ever reached the write.
    const { id: _, owner: __, dateCreated: ___, photos: ____, videosYT: _____, trainingPhoto: ______, flows, techniques, ...dataToUpdate } = data; // Use correct field name
    
    // Extract techniques from flows if flows are being updated
    // CRITICAL: Original app stores FULL technique objects, not just { key: true }
    let extractedTechniques: Record<string, any> | undefined;
    let extractedTechniqueDetails: Record<string, { name: string; isTrainingWikiTech: boolean }> | undefined;
    if (flows && flows.length > 0) {
      extractedTechniques = {};
      extractedTechniqueDetails = {};
      flows.forEach(flow => {
        Object.entries(flow.referencedTechs).forEach(([techId, techRef]) => {
          // Store full technique object (matching original app structure)
          extractedTechniques![techId] = {
            key: techId,
            name: techRef.name,
            sortname: techRef.sortname || techRef.name.toLowerCase(),
            type: techRef.type || 'technique',
            ...(techRef.isTrainingWikiTech && { isTrainingWikiTech: true }),
            ...(techRef.isDefault !== undefined && { isDefault: techRef.isDefault }),
            ...(techRef.user && { user: techRef.user })
          };
          extractedTechniqueDetails![techId] = {
            name: techRef.name,
            isTrainingWikiTech: techRef.isTrainingWikiTech || false
          };
        });
      });
    }
    
    // Add modified timestamp
    const updatesObj = {
      ...dataToUpdate,
      dateModified: Date.now(),
      // Include flows if provided
      ...(flows !== undefined && { flows }),
      // Always extract techniques from flows
      ...(flows !== undefined && { techniques: extractedTechniques || {} })
    };

    if (updatesObj.shownOnFeed !== undefined) {
      (updatesObj as Record<string, any>).shownOnFeeds = updatesObj.shownOnFeed;
    }
    
    await updateData(`/trainings/${id}`, updatesObj);

    // The byId/byUser caches still hold the pre-update training (30-min TTL);
    // drop it so the post-write fetches below return the updated session
    // instead of the stale cached copy
    removeTrainingFromCache(id, existingTraining.owner);

    // Update Personal Wiki with techniques from flows if flows were updated
    if (extractedTechniques && Object.keys(extractedTechniques).length > 0) {
      try {
        // Get the training to find the owner
        const training = await getTrainingById(id);
        if (training) {
          await updateTechniquesFromTrainingFlow(training.owner, Object.keys(extractedTechniques), extractedTechniqueDetails);
        }
      } catch (wikiError) {
        // Log but don't fail the training update if wiki update fails
        logger.error('Error updating personal wiki from training flow:', wikiError);
      }
    }
    
    // Update goal progress if training time was changed
    if (dataToUpdate.totalTrainedTime !== undefined) {
      try {
        const training = await getTrainingById(id);
        if (training) {
          await goalsService.updateGoalProgressFromTraining(training.owner, dataToUpdate.totalTrainedTime, training.date);
        }
      } catch (goalError) {
        // Log but don't fail the training update if goal tracking fails
        logger.error('Error updating goal progress:', goalError);
      }
    }
    
    // Fetch the updated training
    const updatedTraining = await getTrainingById(id);
    if (!updatedTraining) {
      throw new Error('Failed to fetch updated training');
    }
    
    return updatedTraining;
  } catch (error) {
    logger.error(`Error updating training ${id}:`, error);
    throw error;
  }
};

// Delete training
export const deleteTraining = async (id: string): Promise<void> => {
  try {
    // SECURITY: Verify user authorization before allowing deletion
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to delete training');
    }

    // Get training to verify ownership
    const training = await getTrainingById(id);
    if (!training) {
      throw new Error('Training not found');
    }

    // CRITICAL SECURITY CHECK: Only allow users to delete their own trainings
    if (training.owner !== currentUser.uid) {
      throw new Error('Unauthorized: You can only delete your own trainings');
    }
    
    const ownerId = training.owner;
    
    // Delete any associated storage files
    if (training.photos) {
      for (const photo of Object.values(training.photos)) {
        try {
          // Extract file path from URL
          const url = new URL(photo.url);
          const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
          const fileRef = storageRef(storage, path);
          await deleteObject(fileRef);
        } catch (photoError) {
          logger.warn(`Error deleting photo for training ${id}:`, photoError);
          // Continue with other deletions even if some photos fail
        }
      }
    }
    
    // Delete the training record
    await removeData(`/trainings/${id}`);

    // Remove from cache to prevent stale data
    removeTrainingFromCache(id, ownerId);

    // Update user stats asynchronously in background (don't block deletion)
    // This prevents UI freeze during deletion
    updateUserTrainingStats(ownerId).catch(statsError => {
      logger.warn(`Background stats update failed after deleting training ${id}:`, statsError);
      // Don't throw - stats can be recalculated later
    });

    logger.info(`Training ${id} deleted successfully`);
  } catch (error) {
    logger.error(`Error deleting training ${id}:`, error);
    throw error;
  }
};

// Monotonic counter mixed into photo keys so two photos resolving in the same
// millisecond can't collide (a bare Date.now() key silently overwrote the
// first photo). Date.now()*1000 + counter stays a numeric string well below
// Number.MAX_SAFE_INTEGER, so the parseInt-based sorting used for photo keys
// (legacy index keys "0","1",... and timestamp keys) keeps working.
let photoKeySeq = 0;

// Add photo to training
export const addTrainingPhoto = async (
  trainingId: string, 
  file: File, 
  caption?: string, 
  aspectRatio?: number
): Promise<string> => {
  try {
    // Upload the file to Firebase Storage.
    // No leading slash: the native REST upload would create an object
    // literally named "/trainings/..." while getDownloadURL looks up the
    // normalized path, so the upload 404s on iOS/Android.
    const photoPath = `trainings/${trainingId}/photos/${Date.now()}_${file.name}`;
    const photoURL = await uploadFile(photoPath, file);
    
    // Calculate aspect ratio if not provided
    let calculatedAspectRatio = aspectRatio || 1;
    if (!aspectRatio) {
      try {
        calculatedAspectRatio = await calculateImageAspectRatio(file);
      } catch (error) {
        logger.warn('Failed to calculate aspect ratio for training photo:', error);
      }
    }
    
    // Create a new photo entry in the training with a collision-proof,
    // parseInt-sortable key (see photoKeySeq above)
    const photoId = (Date.now() * 1000 + (photoKeySeq++ % 1000)).toString();
    const photoData = {
      url: photoURL,
      aspectRatio: calculatedAspectRatio,
      caption: caption || '',
      dateAdded: Date.now()
    };
    
    await updateData(`/trainings/${trainingId}/photos/${photoId}`, photoData);
    
    return photoId;
  } catch (error) {
    logger.error(`Error adding photo to training ${trainingId}:`, error);
    throw error;
  }
};

// Delete photo from training
export const deleteTrainingPhoto = async (trainingId: string, photoId: string): Promise<void> => {
  try {
    // SECURITY: Verify user authorization before allowing photo deletion
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to delete training photo');
    }

    // Get training to verify ownership
    const training = await getTrainingById(trainingId);
    if (!training) {
      throw new Error('Training not found');
    }

    // CRITICAL SECURITY CHECK: Only allow users to delete photos from their own trainings
    if (training.owner !== currentUser.uid) {
      throw new Error('Unauthorized: You can only delete photos from your own trainings');
    }

    // Get the photo data
    const photoData = await getData(`/trainings/${trainingId}/photos/${photoId}`);
    if (!photoData) {
      throw new Error('Photo not found');
    }
    
    // Delete file from storage
    try {
      // Extract file path from URL
      const url = new URL(photoData.url);
      const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
      const fileRef = storageRef(storage, path);
      await deleteObject(fileRef);
    } catch (storageError) {
      logger.warn(`Error deleting photo file for training ${trainingId}:`, storageError);
      // Continue even if storage deletion fails
    }
    
    // Remove photo reference from training
    await removeData(`/trainings/${trainingId}/photos/${photoId}`);
  } catch (error) {
    logger.error(`Error deleting photo ${photoId} from training ${trainingId}:`, error);
    throw error;
  }
};

// Add YouTube video to training
export const addTrainingVideo = async (
  trainingId: string, 
  youtubeId: string, 
  title?: string
): Promise<string> => {
  try {
    // Create a new video entry in the training with a collision-proof,
    // parseInt-sortable key (see videoKeySeq above)
    const videoId = (Date.now() * 1000 + (videoKeySeq++ % 1000)).toString();

    // Legacy structure stores YouTube IDs under videosYT
    const videoPath = `/trainings/${trainingId}/videosYT/${videoId}`;
    const videoValue = youtubeId.trim();

    await updateData(videoPath, videoValue);

    // Optional metadata can be stored separately if needed
    if (title) {
      try {
        await updateData(`/trainings/${trainingId}/videoMetadata/${videoId}`, {
          title: title,
          dateAdded: Date.now()
        });
      } catch (metaError) {
        logger.warn(`Error storing video metadata for training ${trainingId}:`, metaError);
      }
    }

    return videoId;
  } catch (error) {
    logger.error(`Error adding video to training ${trainingId}:`, error);
    throw error;
  }
};

// Delete video from training
export const deleteTrainingVideo = async (trainingId: string, videoId: string): Promise<void> => {
  try {
    // SECURITY: Verify user authorization before allowing video deletion
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to delete training video');
    }

    // Get training to verify ownership
    const training = await getTrainingById(trainingId);
    if (!training) {
      throw new Error('Training not found');
    }

    // CRITICAL SECURITY CHECK: Only allow users to delete videos from their own trainings
    if (training.owner !== currentUser.uid) {
      throw new Error('Unauthorized: You can only delete videos from your own trainings');
    }

    // Remove video reference from training (legacy path)
    await removeData(`/trainings/${trainingId}/videosYT/${videoId}`);

    // Clean up optional metadata if present
    try {
      await removeData(`/trainings/${trainingId}/videoMetadata/${videoId}`);
    } catch (metaError) {
      logger.warn(`Error removing video metadata for training ${trainingId}:`, metaError);
    }
  } catch (error) {
    logger.error(`Error deleting video ${videoId} from training ${trainingId}:`, error);
    throw error;
  }
};

// Update training visibility
export const updateTrainingVisibility = async (
  trainingId: string, 
  isPublic?: boolean, 
  shownOnFeed?: boolean
): Promise<void> => {
  try {
    // SECURITY: Verify user authorization before allowing visibility updates
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to update training visibility');
    }

    // Get existing training to verify ownership
    const existingTraining = await getTrainingById(trainingId);
    if (!existingTraining) {
      throw new Error('Training not found');
    }

    // CRITICAL SECURITY CHECK: Only allow users to update their own trainings
    if (existingTraining.owner !== currentUser.uid) {
      throw new Error('Unauthorized: You can only update visibility of your own trainings');
    }

    const updates: Record<string, any> = {};
    
    if (isPublic !== undefined) {
      updates.isPublic = isPublic;
    }
    
    if (shownOnFeed !== undefined) {
      updates.shownOnFeed = shownOnFeed;
      updates.shownOnFeeds = shownOnFeed;
    }
    
    if (Object.keys(updates).length > 0) {
      await updateData(`/trainings/${trainingId}`, updates);
    }
  } catch (error) {
    logger.error(`Error updating visibility for training ${trainingId}:`, error);
    throw error;
  }
};

// Like functionality removed - use the unified likes.service.ts instead

// Increment comment count (use correct field name for social function compatibility)
export const incrementCommentCount = async (trainingId: string): Promise<void> => {
  try {
    const training = await getTrainingById(trainingId);
    const currentCommentsCount = training?.commentsCount || training?.commentCount || 0;
    
    await updateData(`/trainings/${trainingId}`, {
      commentsCount: currentCommentsCount + 1  // Use plural form for original app compatibility
    });
  } catch (error) {
    logger.error(`Error incrementing comment count for training ${trainingId}:`, error);
    throw error;
  }
};

// Decrement comment count (use correct field name for social function compatibility)
export const decrementCommentCount = async (trainingId: string): Promise<void> => {
  try {
    const training = await getTrainingById(trainingId);
    const currentCommentsCount = training?.commentsCount || training?.commentCount || 0;
    
    if (currentCommentsCount > 0) {
      await updateData(`/trainings/${trainingId}`, {
        commentsCount: currentCommentsCount - 1  // Use plural form for original app compatibility
      });
    }
  } catch (error) {
    logger.error(`Error decrementing comment count for training ${trainingId}:`, error);
    throw error;
  }
};

// Get training statistics
export const getTrainingStats = async (uid: string): Promise<TrainingStats> => {
  try {
    const trainings = await getTrainings(uid);
    
    if (trainings.length === 0) {
      return {
        totalSessions: 0,
        totalTrainingTime: 0,
        totalRollTime: 0,
        totalDrillingTime: 0,
        totalConditioningTime: 0,
        averageTrainingTime: 0,
        averageRollTime: 0,
        weeklyAverage: 0,
        monthlyAverage: 0,
        streak: {
          current: 0,
          longest: 0
        },
        sessionsLastWeek: 0,
        sessionsLastMonth: 0,
        sessionsThisYear: 0,
        byType: {},
        byLocation: {},
        byInstructor: {}
      };
    }
    
    // Initialize stats
    let totalTrainingTime = 0;
    let totalRollTime = 0;
    let totalDrillingTime = 0;
    let totalConditioningTime = 0;
    
    const now = Date.now();
    const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000;
    const oneMonthAgo = now - 30 * 24 * 60 * 60 * 1000;
    const startOfYear = new Date(new Date().getFullYear(), 0, 1).getTime();
    
    let sessionsLastWeek = 0;
    let sessionsLastMonth = 0;
    let sessionsThisYear = 0;
    
    // Track by type, location, and instructor
    const byType: Record<string, number> = {};
    const byLocation: Record<string, number> = {};
    const byInstructor: Record<string, number> = {};
    
    // Sorted dates for streak calculation
    const trainingDates = trainings
      .map(t => new Date(t.date).setHours(0, 0, 0, 0))
      .sort((a, b) => a - b);
    
    // Remove duplicates (multiple trainings on same day count as 1 for streak)
    const uniqueDates = [...new Set(trainingDates)];
    
    // Calculate streak
    let currentStreak = 0;
    let longestStreak = 0;

    // Pre-aggregate all training stats to avoid loop functions
    trainings.forEach(training => {
      totalTrainingTime += training.totalTrainedTime;
      totalRollTime += training.rollTime || 0;
      // totalDrillingTime += training.drillingTime || 0; // Not in original app
      // totalConditioningTime += training.conditioningTime || 0; // Not in original app
      
      // Count by type
      const type = training.type;
      byType[type] = (byType[type] || 0) + 1;
      
      // Count by location
      if (training.location) {
        const locationName = training.location.name;
        byLocation[locationName] = (byLocation[locationName] || 0) + 1;
      }
      
      // Count by instructor (original format is string uid)
      if (training.instructor) {
        const instructorId = training.instructor;
        byInstructor[instructorId] = (byInstructor[instructorId] || 0) + 1;
      }
      
      // Recent sessions
      if (training.date >= oneWeekAgo) {
        sessionsLastWeek++;
      }
      
      if (training.date >= oneMonthAgo) {
        sessionsLastMonth++;
      }
      
      if (training.date >= startOfYear) {
        sessionsThisYear++;
      }
    });

    // Calculate days from uniqueDates (streak calculation only)
    for (let i = 0; i < uniqueDates.length; i++) {
      const currentDate = uniqueDates[i];
      
      // Streak calculation
      if (i === 0) {
        // First date
        currentStreak = 1;
      } else {
        const prevDate = uniqueDates[i - 1];
        const dayDiff = (currentDate - prevDate) / (24 * 60 * 60 * 1000);
        
        if (dayDiff === 1) {
          // Consecutive day
          currentStreak++;
        } else {
          // Streak broken
          if (currentStreak > longestStreak) {
            longestStreak = currentStreak;
          }
          currentStreak = 1;
        }
      }
    }
    
    // Check if current streak is the longest
    if (currentStreak > longestStreak) {
      longestStreak = currentStreak;
    }
    
    // Check if the streak is current
    const lastTrainingDate = uniqueDates[uniqueDates.length - 1];
    const today = new Date().setHours(0, 0, 0, 0);
    const yesterday = today - 24 * 60 * 60 * 1000;
    
    // If the last training wasn't today or yesterday, reset current streak
    if (lastTrainingDate < yesterday) {
      currentStreak = 0;
    }
    
    // Calculate averages
    const totalSessions = trainings.length;
    const averageTrainingTime = totalSessions > 0 ? totalTrainingTime / totalSessions : 0;
    const averageRollTime = totalSessions > 0 ? totalRollTime / totalSessions : 0;
    
    // Calculate weekly/monthly averages
    const firstTrainingDate = trainingDates[0];
    const totalWeeks = Math.max(1, Math.ceil((now - firstTrainingDate) / (7 * 24 * 60 * 60 * 1000)));
    const totalMonths = Math.max(1, Math.ceil((now - firstTrainingDate) / (30 * 24 * 60 * 60 * 1000)));
    
    const weeklyAverage = totalSessions / totalWeeks;
    const monthlyAverage = totalSessions / totalMonths;
    
    return {
      totalSessions,
      totalTrainingTime,
      totalRollTime,
      totalDrillingTime,
      totalConditioningTime,
      averageTrainingTime,
      averageRollTime,
      weeklyAverage,
      monthlyAverage,
      streak: {
        current: currentStreak,
        longest: longestStreak
      },
      sessionsLastWeek,
      sessionsLastMonth,
      sessionsThisYear,
      byType,
      byLocation,
      byInstructor
    };
  } catch (error) {
    logger.error(`Error getting training stats for user ${uid}:`, error);
    throw error;
  }
};

// Get most used techniques
export const getMostUsedTechniques = async (uid: string, limit: number = 10): Promise<{
  id: string;
  name: string;
  count: number;
  type: string;
}[]> => {
  try {
    const trainings = await getTrainings(uid);
    
    // Collect techniques
    const techniqueCounts: Record<string, {
      id: string;
      name: string;
      count: number;
      type: string;
    }> = {};
    
    trainings.forEach(training => {
      if (!training.techniques) return;
      
      // Original format is Record<string, boolean>
      Object.keys(training.techniques).forEach(techniqueId => {
        if (training.techniques![techniqueId]) { // Only count if true
          if (!techniqueCounts[techniqueId]) {
            techniqueCounts[techniqueId] = {
              id: techniqueId,
              name: techniqueId, // In original, we'd need to resolve the name
              count: 0,
              type: 'unknown' // In original, we'd need to resolve the type
            };
          }
          
          techniqueCounts[techniqueId].count++;
        }
      });
    });
    
    // Convert to array and sort by count
    const sortedTechniques = Object.values(techniqueCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
    
    return sortedTechniques;
  } catch (error) {
    logger.error(`Error getting most used techniques for user ${uid}:`, error);
    throw error;
  }
};

// Update user's training stats
export const updateUserTrainingStats = async (uid: string): Promise<void> => {
  try {
    const stats = await getTrainingStats(uid);
    
    await updateUserStats(uid, {
      totalTrainings: stats.totalSessions,
      totalTime: stats.totalTrainingTime,
      totalRollTime: stats.totalRollTime
    });
  } catch (error) {
    logger.error(`Error updating user training stats for ${uid}:`, error);
    throw error;
  }
};

// Subscribe to a training
export const subscribeToTraining = (trainingId: string, callback: (training: Training | null) => void): (() => void) => {
  const unsubscribe = subscribeToData(`/trainings/${trainingId}`, (data) => {
    if (!data) {
      callback(null);
      return;
    }
    
    callback({
      id: trainingId,
      ...data
    });
  });
  
  return unsubscribe;
};

// Subscribe to all user trainings
export const subscribeToUserTrainings = (
  uid: string, 
  callback: (trainings: Training[]) => void,
  options?: QueryOptions
): (() => void) => {
  // This is more complex as we need to listen for all trainings and filter
  // We'll subscribe to the entire trainings node and filter by owner
  const unsubscribe = subscribeToData('/trainings', async (data) => {
    if (!data) {
      callback([]);
      return;
    }
    
    try {
      // Filter and process trainings
      let userTrainings: Training[] = Object.entries(data)
        .filter(([_, training]: [string, any]) => training.owner === uid)
        .map(([id, trainingData]: [string, any]) => ({
          id,
          ...trainingData
        }));
        
      // Apply options filtering
      if (options) {
        // Apply filters here (similar to getTrainings)
        if (options.types && options.types.length > 0) {
          userTrainings = userTrainings.filter(t => options.types?.includes(t.type));
        }
        
        if (options.startAfter) {
          userTrainings = userTrainings.filter(t => t.date >= options.startAfter!);
        }
        
        if (options.endBefore) {
          userTrainings = userTrainings.filter(t => t.date <= options.endBefore!);
        }
        
        // Apply ordering
        const orderField = options.orderBy || 'date';
        const isAsc = options.orderDirection === 'asc';
        
        userTrainings.sort((a, b) => {
          const aValue = a[orderField as keyof Training] as number;
          const bValue = b[orderField as keyof Training] as number;
          
          if (isAsc) {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        });
        
        // Apply limit
        if (options.limit) {
          userTrainings = userTrainings.slice(0, options.limit);
        }
      } else {
        // Default sort by date descending
        userTrainings.sort((a, b) => b.date - a.date);
      }
      
      callback(userTrainings);
    } catch (error) {
      logger.error('Error processing training subscription data:', error);
    }
  });
  
  return unsubscribe;
};
